import useCurrency from '@/composables/useCurrency'
import type { DirectiveBinding } from 'vue'
import type { PermissionValue } from '@/types/Permissions'
import { usePermissionCache } from '~/composables/usePermissions'
interface PermissionDirectiveValue {
	permission?: PermissionValue | readonly PermissionValue[]
	fallback?: () => void
	hideMode?: 'display' | 'visibility' | 'remove'
}
export default defineNuxtPlugin(nuxtApp => {
	// Core number formatting logic
	function formatNumberInput(target: HTMLInputElement, isTel: boolean = false) {
		let v = target.value.replace(/[^\d+]+/g, '')
		if (isTel) {
			if (v.startsWith('234')) {
				target.maxLength = 13
				v = v.slice(0, 13)
			} else {
				target.maxLength = 11
			}
		}
		target.value = v
	}

	nuxtApp.vueApp.directive('number', {
		created(el: HTMLDivElement, binding) {
			if (typeof binding.value === 'boolean' && binding.value) {
				el.inputMode = 'numeric'
				el.addEventListener('input', (t: Event) => {
					const target = t.target as HTMLInputElement
					formatNumberInput(target)
				})
			}
		}
	})

	nuxtApp.vueApp.directive('tel', {
		created(el: HTMLDivElement, binding) {
			if (typeof binding.value === 'boolean' && binding.value) {
				el.inputMode = 'tel'
				el.addEventListener('input', (t: Event) => {
					const target = t.target as HTMLInputElement
					formatNumberInput(target, true)
					let v = target.value
					if (!(v.startsWith('0') || v.startsWith('2'))) {
						target.value = ''
						return
					}
					if (v.startsWith('234')) {
						// Remove any leading zeros after +234
						v = '234' + v.slice(3).replace(/^0+/, '')
						target.value = v
					}
				})
			}
		}
	})

	nuxtApp.vueApp.directive('naira', {
		created(el: HTMLInputElement, binding) {
			if (!binding.value) return

			el.inputMode = 'numeric'

			el.addEventListener('input', (e: Event) => {
				const target = e.target as HTMLInputElement
				const { selectionStart } = target
				const rawValue = target.value
				const sanitized = rawValue.replace(/[^0-9.]/g, '')
				const parts = sanitized.split('.')
				if (parts.length > 2) {
					target.value = parts[0] + '.' + parts.slice(1).join('')
					return
				}
				const formatted = useCurrency().currencyToNaira(sanitized) || ''
				const diff = formatted.length - rawValue.length
				const newCursorPos = (selectionStart || 0) + diff
				target.value = formatted
				requestAnimationFrame(() => {
					target.setSelectionRange(newCursorPos, newCursorPos)
				})
			})
		}
	})

	nuxtApp.vueApp.directive('format', {
		created(el: HTMLDivElement, binding) {
			if (typeof binding.value === 'boolean' && binding.value) {
				el.inputMode = 'numeric'
				el.addEventListener('input', event => {
					// The idea is to format a number that comes from a string and parse it back to a formatted string in the input
					const inputTarget = event.target as HTMLInputElement
					const value = parseFloat(inputTarget.value.replace(/[^\d.-]/g, '')) || 0
					const formattedValue = value.toLocaleString('en-US')
					inputTarget.value = formattedValue
				})

				el.addEventListener('keyup', event => {
					const key = event.code
					const inputValue = event.target as HTMLInputElement
					// The idea is to increment a number even when it is formatted, e.g (1,000) can be 1,001 when a key stroke is hit
					if (key === 'arrowup' || key === 'arrowdown') {
						event.preventDefault()
						let value = parseFloat(inputValue.value.replace(/[^\d.-]/g, '')) || 0

						if (key === 'arrowup') {
							value += 1
						} else if (key === 'arrowdown') {
							value = Math.max(value - 1, 0) // Decrement, but don't go below -1
						}

						// Format the value before updating the input
						const formattedValue = value.toLocaleString('en-US')

						inputValue.value = formattedValue
					}
				})
			}
		}
	})

	function updateElementVisibility(
		el: HTMLElement,
		binding: DirectiveBinding<PermissionValue | readonly PermissionValue[]>
	) {
		const { can, canAll, canAny } = usePermissionCache()

		const permissions = binding.value
		const modifiers = binding.modifiers

		if (!permissions) {
			el.style.display = ''
			return
		}

		let hasAccess: boolean

		if (Array.isArray(permissions)) {
			hasAccess = modifiers.all
				? canAll(permissions as PermissionValue[])
				: canAny(permissions as PermissionValue[])
		} else {
			hasAccess = can(permissions as PermissionValue)
		}

		const hideMode = (modifiers.visibility ? 'visibility' : 'display') as 'visibility' | 'display'

		if (!hasAccess) {
			if (hideMode === 'visibility') {
				el.style.visibility = 'hidden'
			} else {
				el.style.display = 'none'
			}
			el.setAttribute('data-permission-denied', 'true')
		} else {
			if (hideMode === 'visibility') {
				el.style.visibility = 'visible'
			} else {
				el.style.display = ''
			}
			el.removeAttribute('data-permission-denied')
		}
	}
	nuxtApp.vueApp.directive('permission', {
		mounted(el: HTMLElement, binding: DirectiveBinding<PermissionValue | readonly PermissionValue[]>) {
			updateElementVisibility(el, binding)
		},
		updated(el: HTMLElement, binding: DirectiveBinding<PermissionValue | readonly PermissionValue[]>) {
			updateElementVisibility(el, binding)
		},
		unmounted(el: HTMLElement) {
			el.removeAttribute('data-permission-denied')
		}
	})
})
